import { Form } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FC } from 'react';
import { useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { hasRole } from '../../../../utils/roles';
import { FormContainer } from '../../../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../../../utils/validations';
import { FieldWithAction } from '../../../common/form/FieldWithAction';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { SupplierEmployeeAutocomplete } from '../../../common/form/SupplierEmployeeAutocomplete';
import { QcsTypography } from '../../../common/basic/QcsTypography';
import { getFirstLastNameObj } from '../../../../utils/format';
import { useInvitation } from './invitationFunctions';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { ConfirmInvitationRequest } from '@api/models/invitation/ConfirmInvitationRequest';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';
import { invitationService } from '@api/services/services';
import { ShowError } from '../../../ShowError';
import { resources } from '@api/resources';
import { Box, Chip, Paper } from '@mui/material';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FaIcon } from '../../../common/FaIcon';
import { PathKey, useNavigation } from '../../../../utils/urlPaths';
import { sectionLabelSx } from '../styles';

interface InvitationDetailForm {
  responsiblePerson?: ReferenceDto;
}

export const InvTabSupplier: FC = () => {
  const { t, invitation, callWithLock, reloadInvitation } = useInvitation();
  const { navigate, currentPath } = useNavigation();
  const identity = useAppSelector(selectIdentity);

  const canChangeResponsiblePerson = hasRole(identity.roles, [
    UserRole.AdminQcs,
    UserRole.ManagerOsah,
    UserRole.ManagerWorkplace,
    UserRole.ExternalManager,
  ]);

  const handleSubmit = async (data: InvitationDetailForm) => {
    await callWithLock(async () => {
      const data2: ConfirmInvitationRequest = {
        responsiblePerson: data.responsiblePerson!,
      };

      await invitationService.confirmInvitationDetails(invitation.id!, data2);
      await reloadInvitation();
    });
  };

  const supplier = invitation.supplier;
  const address = supplier?.address;

  const handleSupplierClick = () => {
    if (supplier?.id) {
      navigate(PathKey.SupplierDetail, { supplierId: supplier.id }, { backTo: currentPath });
    }
  };

  return (
    <>
      <FormikDebounce<InvitationDetailForm>
        enableReinitialize
        initialValues={{
          responsiblePerson: invitation.responsiblePerson
            ? {
                id: invitation.responsiblePerson!.id!,
                name: getFirstLastNameObj(invitation.responsiblePerson),
              }
            : undefined,
        }}
        validationSchema={Yup.object().shape({
          responsiblePerson: validations.objectRequired(t),
        })}
        onSubmit={handleSubmit}
      >
        <Form>
          <FormContainer>
            <Paper
              variant="outlined"
              onClick={supplier?.id ? handleSupplierClick : undefined}
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                borderColor: 'divider',
                transition: 'border-color 0.2s',
                ...(supplier?.id && {
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <QcsTypography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {supplier?.name || '—'}
                  </QcsTypography>
                  {supplier?.id && (
                    <FaIcon icon={faUpRightFromSquare} width={13} height={13} style={{ color: 'inherit', opacity: 0.4, flexShrink: 0 }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                  {supplier?.ico && (
                    <Chip label={`${t('invitation.ico')}: ${supplier.ico}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                  )}
                  {supplier?.vat && (
                    <Chip label={`${t('invitation.vat')}: ${supplier.vat}`} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                  )}
                </Box>
              </Box>

              {(address?.street || address?.city || address?.country) && (
                <QcsTypography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {[address?.street, [address?.postalCode, address?.city].filter(Boolean).join(' '), address?.country].filter(Boolean).join(', ')}
                </QcsTypography>
              )}
            </Paper>
            <QcsTypography variant="body2" sx={sectionLabelSx}>
              {t('invitation.responsiblePerson')}
            </QcsTypography>
            {canChangeResponsiblePerson ? (
              <FieldWithAction action={<SubmitButton text={t('invitation.detailButtonSave')} />}>
                <SupplierEmployeeAutocomplete
                  name="responsiblePerson"
                  label={t('invitation.responsiblePerson')}
                  required
                  supplierId={supplier?.id}
                  role={UserRole.ExternalManager}
                  state={[UserState.Active, UserState.Invited]}
                />
              </FieldWithAction>
            ) : (
              <QcsTypography variant="body2">{invitation.responsiblePerson ? getFirstLastNameObj(invitation.responsiblePerson) : '—'}</QcsTypography>
            )}
          </FormContainer>
        </Form>
      </FormikDebounce>

      <ShowError resource={resources.confirmInvitationDetails} />
    </>
  );
};
